// High-level conversion tracking — single entry point for components.
// Each helper fires the right event on each platform with the right params.
// Both platforms are consent-gated at the lower level (analytics.js / meta-pixel.js)
// so this module can be called from anywhere without checking consent first.
import { logGAEvent, enableAnalytics } from './analytics.js';
import { trackPixelEvent, enableMetaPixel } from './meta-pixel.js';
import { trackAdsConversion, updateGoogleConsent } from './google-ads.js';

// Google Ads conversion labels. The full send_to is `<conversion-id>/<label>`
// — the conversion-id portion is set once when gtag.js is configured in index.html.
const GADS_CONVERSION_CALENDLY_BOOKED = 'AW-18148879279/mPq4CPr04q8cEK_Xh85D';
const GADS_CONVERSION_HUBSPOT_FORM   = 'AW-18148879279/apbHCNO1yK8cEK_Xh85D';

const UTM_KEY = 'aa_utm_v1';
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'gclid', 'fbclid', 'msclkid'];

// ─────────────────────────────────────────────────────────────────────────────
// UTM CAPTURE — persist ad attribution across the session so it travels with
// the lead all the way to HubSpot / Calendly URLs. Read once on app boot.
// ─────────────────────────────────────────────────────────────────────────────
export function captureUtmFromUrl() {
  if (typeof window === 'undefined') return;
  try {
    const url = new URL(window.location.href);
    const captured = {};
    let hasAny = false;
    for (const k of UTM_KEYS) {
      const v = url.searchParams.get(k);
      if (v) { captured[k] = v; hasAny = true; }
    }
    if (!hasAny) return;
    const referrer = (document.referrer || '').slice(0, 200);
    const payload = { ts: Date.now(), captured, landing: window.location.pathname, referrer };
    localStorage.setItem(UTM_KEY, JSON.stringify(payload));
  } catch { /* noop */ }
}

export function getStoredUtm() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.ts > UTM_TTL_MS) {
      localStorage.removeItem(UTM_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

// Append captured UTM params (and referrer info) onto an outbound URL — used to
// carry attribution to Calendly so source/campaign show up in the booking record.
export function withUtm(targetUrl) {
  const utm = getStoredUtm();
  if (!utm || !utm.captured) return targetUrl;
  try {
    const u = new URL(targetUrl);
    for (const [k, v] of Object.entries(utm.captured)) u.searchParams.set(k, v);
    if (utm.landing) u.searchParams.set('aa_landing', utm.landing);
    return u.toString();
  } catch {
    return targetUrl;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// CONVERSION EVENTS — fired on both Meta Pixel + GA4 with platform-appropriate
// parameters. Each conversion helper calls ensureTrackingConsent() first so the
// event is guaranteed to fire even if the CTA-click-wrap didn't grant consent
// up-front (e.g. user landed on Calendly via a deep link or back-button flow).
// ─────────────────────────────────────────────────────────────────────────────

// Click-wrap consent backstop: at the moment of a definitive conversion (form
// submit or meeting booking) we treat the user's deliberate action as consent
// for conversion tracking, and grant it on EVERY channel.
//
// This runs in two independent steps so it is reliable on every route:
//
// 1. If the CookieBanner bridge is loaded (window.AAConsent), persist + broadcast
//    the choice through it — so the banner hides and the stored consent is
//    remembered for future page loads. The bridge's listener also enables the
//    SDKs and pushes gtag('consent','update','granted').
//
// 2. Grant on each channel DIRECTLY too. This is the critical part: the HubSpot
//    conversion fires from GuidePage (/guide) which does NOT load CookieBanner,
//    so window.AAConsent is undefined there and step 1 is skipped entirely.
//    Without step 2 the Meta Lead, GA4 generate_lead and Google Ads conversion
//    all fired with no consent — Meta/GA4 no-op'd and the Ads ping went out as
//    'denied'. updateGoogleConsent + enableMetaPixel + enableAnalytics are all
//    idempotent, so calling them here (possibly in addition to step 1) is safe.
//    They synchronously set the consent/active flags, so the track*() calls that
//    follow are guaranteed to fire (and the gtag consent update is queued onto
//    the dataLayer BEFORE the conversion event).
function ensureTrackingConsent() {
  if (typeof window === 'undefined') return;
  if (window.AAConsent) {
    const c = window.AAConsent.current();
    if (!c.functional || !c.statistics || !c.marketing) {
      window.AAConsent.set({ functional: true, statistics: true, marketing: true });
    }
  }
  updateGoogleConsent({ statistics: true, marketing: true });
  enableMetaPixel();
  enableAnalytics();
}

// Narrow click-wrap for a STATISTICS-only signal (e.g. add-to-calendar on the
// standalone /webinar-calendar page). The deliberate click is treated as
// consent for analytics, but — unlike ensureTrackingConsent — we do NOT grant
// Marketing here (no ad pixels for a calendar add). Marketing/functional are
// preserved as-is; only Statistics is flipped on.
function ensureStatisticsConsent() {
  if (typeof window === 'undefined') return;
  if (window.AAConsent) {
    const c = window.AAConsent.current();
    if (!c.statistics) {
      // Spread `c` first so functional/marketing/necessary are preserved —
      // AAConsent.set() merges over all-false defaults, so omitting them would
      // silently revoke Marketing (and disable the Meta Pixel).
      window.AAConsent.set({ ...c, statistics: true });
    }
  }
  // Direct enable as a backstop (idempotent) in case the AAConsent bridge
  // isn't wired on this route.
  enableAnalytics();
}

// Lead — submitted the HubSpot guide form (the user gave us their info).
// Fired from GuidePage (/guide), the thank-you page HubSpot redirects to after a
// successful submit. NOTE: /guide does NOT load CookieBanner, so the consent
// bridge (window.AAConsent) is absent here — ensureTrackingConsent() grants
// every channel directly to compensate (see its body). GuidePage waits a short
// grace period before redirecting to the PDF so the beacons reach the servers.
export function trackLead({ value, currency = 'EUR', method = 'guide_form' } = {}) {
  // Grant consent on all channels BEFORE the events fire. ensureTrackingConsent
  // pushes gtag('consent','update','granted') onto the dataLayer ahead of the
  // trackAdsConversion call below, so the Ads conversion is recorded as granted.
  ensureTrackingConsent();
  trackPixelEvent('Lead', {
    content_name: 'App Academy Guide',
    content_category: 'Lead Magnet',
    ...(value !== undefined ? { value, currency } : {}),
  });
  logGAEvent('generate_lead', {
    method,
    ...(value !== undefined ? { value, currency } : {}),
  });
  trackAdsConversion(GADS_CONVERSION_HUBSPOT_FORM);
}

// Webinar registration — fired on the /webinar-thank-you page, which is the
// landing page ActiveCampaign redirects to after a successful form submit.
// Mirrors the trackLead pattern: ensureTrackingConsent() flips consent on all
// channels first so Meta/GA/Ads all receive the event with consent granted.
// NOTE: a dedicated Google Ads conversion label for webinar registrations is
// not configured yet — we reuse the HubSpot/form conversion id as a stand-in.
// Replace GADS_CONVERSION_HUBSPOT_FORM below with a webinar-specific label
// once the conversion exists in Google Ads.
// eventId — pass a value derived on both client and server from the same
// email hash so Meta explicitly deduplicates the browser Pixel event and the
// CAPI event. Without eventId, Meta falls back to fuzzy dedup which is only
// ~60-80 % accurate — with matching eventId, dedup is 100 %.
export function trackWebinarRegistration({ method = 'webinar_form', eventId } = {}) {
  ensureTrackingConsent();

  // Meta Lead — fire the BROWSER Lead ONLY when we have an event_id to dedupe
  // against the server-side CAPI Lead. Without an event_id the two can't be
  // deduplicated and Meta double-counts (the server always fires its own Lead
  // from the AC webhook). Skipping the browser Lead here means the lead is
  // still counted exactly once — via the server — just without the browser
  // match signal (fbp/fbc). This is the safety net: even if the email-hash
  // capture ever fails, we can never double-count Leads.
  if (eventId) {
    trackPixelEvent('Lead', {
      content_name: 'AI App Academy Webinar',
      content_category: 'Webinar Registration',
    }, { eventID: eventId });
  }

  // CompleteRegistration is browser-only (no server counterpart) → always fire,
  // no double-count risk. Carries the event_id when available for good measure.
  trackPixelEvent('CompleteRegistration', {
    content_name: 'AI App Academy Webinar',
    content_category: 'Webinar Registration',
  }, eventId ? { eventID: eventId } : {});

  // GA4 + Google Ads are browser-only (the CAPI function sends to Meta only),
  // so they always fire — no dedup concern.
  logGAEvent('generate_lead', { method });
  logGAEvent('webinar_registration', { method });
  trackAdsConversion(GADS_CONVERSION_HUBSPOT_FORM);
}

// Add-to-calendar — fired when a registered attendee clicks one of the
// calendar-provider cards on /webinar-thank-you. GA4-only by design: this
// is a soft micro-conversion (intent signal), not a hard lead event that
// belongs on the Meta Pixel.
export function trackCalendarAdd({ provider, source } = {}) {
  // Click-wrap: the deliberate calendar-add click grants Statistics consent so
  // this event is captured even on pages that don't prime consent up-front
  // (e.g. /webinar-calendar reached cold from a reminder e-mail).
  ensureStatisticsConsent();
  logGAEvent('add_to_calendar', {
    calendar_provider: provider,
    event_name: 'AI App Academy Webinar',
    ...(source ? { source } : {}),
  });
}

// Contact — booked a call via Calendly. Fired by the postMessage listener.
// Fires BOTH Meta Lead AND Meta Contact for the same booking — Lead for ad
// optimization (broader signal Meta uses well in B2B), Contact for the more
// specific funnel-stage event in Events Manager / Conversions reporting.
export function trackContact({ method = 'calendly', eventType } = {}) {
  // ensureTrackingConsent() flips AAConsent to all-granted, which synchronously
  // triggers updateGoogleConsent() and pushes a gtag('consent','update',granted)
  // onto the dataLayer BEFORE trackAdsConversion below — so the conversion
  // event is recorded with consent already granted and no signals are dropped.
  ensureTrackingConsent();
  trackPixelEvent('Lead', {
    content_name: 'Infogespräch App Academy',
    content_category: 'Meeting Booked',
  });
  trackPixelEvent('Contact', {
    content_name: 'Infogespräch App Academy',
    ...(eventType ? { content_category: eventType } : {}),
  });
  logGAEvent('generate_lead', {
    method: 'meeting_booking',
    meeting_provider: method,
  });
  logGAEvent('book_meeting', {
    provider: method,
    ...(eventType ? { event_type: eventType } : {}),
  });
  trackAdsConversion(GADS_CONVERSION_CALENDLY_BOOKED);
}

// Schedule intent — Calendly popup was opened (CTA click). User has NOT booked
// yet — this fires regardless of whether a booking follows. Useful funnel signal:
// how many open the popup vs. how many actually schedule.
//
// Meta event mapping:
// - SubmitApplication = user "started" the booking flow (semantically the closest
//   standard event for "began the conversion path"). Meta optimizes ad delivery
//   well against this signal in B2B.
// - The actual booking completion fires Lead + Contact (see trackContact).
//
// GA4 event mapping:
// - begin_checkout = standard recommended event for starting a conversion flow.
// - schedule_intent = custom event with extra location/UTM context for our funnel.
export function trackScheduleIntent({ location } = {}) {
  trackPixelEvent('SubmitApplication', {
    content_name: 'Calendly opened',
    content_category: 'Infogespräch',
  });
  logGAEvent('begin_checkout', {
    items: [{ item_name: 'Infogespräch', item_category: 'meeting_booking' }],
  });
  logGAEvent('schedule_intent', { cta_location: location });
}

// CTA click — generic primary-button click. GA4 only (would be too noisy on Meta).
export function trackCTAClick({ label, location, destination } = {}) {
  logGAEvent('cta_click', {
    cta_label: label,
    cta_location: location,
    ...(destination ? { destination } : {}),
  });
}

// FAQ open — which question was expanded. GA4 only.
export function trackFAQOpen({ question, position } = {}) {
  logGAEvent('faq_open', { question, ...(position !== undefined ? { position } : {}) });
}

// View item / package interest — GA4's `view_item` is the standard event for this.
export function trackPackageView({ packageName, position } = {}) {
  logGAEvent('view_item', {
    item_name: packageName,
    item_category: 'package',
    ...(position !== undefined ? { index: position } : {}),
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// CALENDLY POSTMESSAGE LISTENER — Calendly emits postMessages from its iframe;
// `event_scheduled` means a booking was confirmed. Attached once at app boot.
// Listener itself is consent-agnostic; trackContact() checks consent for each platform.
// ─────────────────────────────────────────────────────────────────────────────
let calendlyListenerAttached = false;
const DEBUG_CALENDLY = true;  // logs every Calendly postMessage to console for debugging

export function initCalendlyListener() {
  if (calendlyListenerAttached || typeof window === 'undefined') return;
  calendlyListenerAttached = true;

  window.addEventListener('message', (e) => {
    // Calendly's docs declare the canonical origin as https://calendly.com.
    // We also accept *.calendly.com because the popup iframe can be served
    // from auth.calendly.com or scheduling.calendly.com depending on flow.
    const origin = e.origin || '';
    const isCalendlyOrigin = origin === 'https://calendly.com' || origin.endsWith('.calendly.com');
    if (!isCalendlyOrigin) return;

    const data = e.data;
    if (!data || typeof data !== 'object') return;

    const eventName = data.event;
    if (typeof eventName !== 'string' || eventName.indexOf('calendly.') !== 0) return;

    if (DEBUG_CALENDLY && typeof console !== 'undefined') {
      console.log('[Calendly]', eventName, data.payload || data);
    }

    if (eventName === 'calendly.event_scheduled') {
      const payload = data.payload || {};
      const eventType = (payload.event_type && payload.event_type.name)
        || (payload.event && payload.event.name)
        || undefined;
      trackContact({ method: 'calendly', eventType });
      if (DEBUG_CALENDLY && typeof console !== 'undefined') {
        console.log('[Calendly] booking confirmed → fired trackContact (Meta Lead+Contact, GA4 generate_lead+book_meeting)');
      }
    }
  });
}
